import * as THREE from 'three';

export type Accessory = 'shades' | 'party' | 'pirate' | 'beanie' | 'tophat' | 'crown';

/** How a duck looks: colours, finish and an optional hat or accessory. */
export type DuckLook = {
  body?: string;
  beak?: string;
  /** Head colour if it differs from the body (e.g. a mallard's green head). */
  head?: string;
  /** 0 = rubber, higher = shiny metal (the gold duck). */
  metalness?: number;
  accessory?: Accessory;
};

export type Skin = DuckLook & { id: string; name: string; price: number };

/**
 * The duck shop. Colours steer clear of the rivals' blue, pink and green so
 * the player never looks like Bill, Rosie or Sprout.
 */
export const SKINS: readonly Skin[] = [
  { id: 'classic', name: 'Classic', price: 0 },
  { id: 'shades', name: 'Cool Duck', price: 60, accessory: 'shades' },
  { id: 'party', name: 'Party Duck', price: 90, accessory: 'party' },
  { id: 'mallard', name: 'Mallard', price: 120, body: '#9a7650', head: '#1e7a45', beak: '#f2b70f' },
  { id: 'pirate', name: 'Captain Quack', price: 160, accessory: 'pirate' },
  { id: 'snowy', name: 'Snowy', price: 200, body: '#f3f6f8', accessory: 'beanie' },
  { id: 'midnight', name: 'Midnight', price: 250, body: '#3d2f70', beak: '#ffcf3f' },
  { id: 'dapper', name: 'Dapper', price: 300, accessory: 'tophat' },
  { id: 'royal', name: 'Royal Gold', price: 450, body: '#e8b62a', beak: '#ff9a1c', metalness: 0.65, accessory: 'crown' },
];

export function skinById(id: string): Skin {
  return SKINS.find((skin) => skin.id === id) ?? SKINS[0];
}

/** Flat-shaded toy material, matching the river's low-poly props. */
function toy(color: string, metalness = 0, roughness = 0.5): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, metalness, roughness, flatShading: true });
}

/** Builds a hat or accessory in duck-model space (head centre at 0, 0.66, -0.3; facing -z). */
export function buildAccessory(kind: Accessory): THREE.Group {
  const group = new THREE.Group();
  if (kind === 'shades') {
    const lens = toy('#15161a', 0.2, 0.2);
    for (const side of [-1, 1]) {
      const glass = new THREE.Mesh(new THREE.BoxGeometry(0.17, 0.1, 0.04), lens);
      glass.position.set(side * 0.12, 0.76, -0.55);
      glass.rotation.y = side * 0.25;
      group.add(glass);
    }
    const bridge = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.025, 0.03), lens);
    bridge.position.set(0, 0.78, -0.58);
    group.add(bridge);
  } else if (kind === 'party') {
    const cone = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.34, 8), toy('#8a5cff'));
    cone.position.set(0.05, 1.08, -0.3);
    cone.rotation.z = -0.25;
    const bobble = new THREE.Mesh(new THREE.IcosahedronGeometry(0.06, 0), toy('#ffe066'));
    bobble.position.set(0.09, 1.25, -0.3);
    const band = new THREE.Mesh(new THREE.TorusGeometry(0.12, 0.025, 5, 10), toy('#ffe066'));
    band.rotation.x = Math.PI / 2;
    band.rotation.y = 0.25;
    band.position.set(0.03, 0.97, -0.3);
    group.add(cone, bobble, band);
  } else if (kind === 'pirate') {
    const black = toy('#1d1d22');
    const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.06, 10), black);
    brim.scale.set(1.45, 1, 0.75);
    brim.position.set(0, 0.95, -0.28);
    const crown = new THREE.Mesh(new THREE.SphereGeometry(0.22, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), black);
    crown.scale.set(1.15, 0.9, 0.9);
    crown.position.set(0, 0.96, -0.28);
    const skull = new THREE.Mesh(new THREE.IcosahedronGeometry(0.045, 0), toy('#ffffff'));
    skull.position.set(0, 1.04, -0.47);
    group.add(brim, crown, skull);
  } else if (kind === 'beanie') {
    const knit = toy('#e8463a');
    const cap = new THREE.Mesh(new THREE.SphereGeometry(0.29, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), knit);
    cap.position.set(0, 0.8, -0.3);
    const rim = new THREE.Mesh(new THREE.TorusGeometry(0.28, 0.05, 6, 14), toy('#ffffff'));
    rim.rotation.x = Math.PI / 2;
    rim.position.set(0, 0.82, -0.3);
    const bobble = new THREE.Mesh(new THREE.IcosahedronGeometry(0.08, 0), toy('#ffffff'));
    bobble.position.set(0, 1.12, -0.3);
    group.add(cap, rim, bobble);
  } else if (kind === 'tophat') {
    const black = toy('#1d1d22', 0, 0.35);
    const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.27, 0.27, 0.03, 12), black);
    brim.position.set(0, 0.93, -0.3);
    const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.17, 0.3, 12), black);
    tube.position.set(0, 1.09, -0.3);
    const band = new THREE.Mesh(new THREE.CylinderGeometry(0.172, 0.172, 0.06, 12), toy('#c8323a'));
    band.position.set(0, 0.98, -0.3);
    group.add(brim, tube, band);
    group.rotation.z = -0.12;
    group.position.x = 0.1;
  } else {
    const gold = toy('#ffcc33', 0.8, 0.25);
    const ring = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.17, 0.1, 10, 1, true), gold);
    ring.position.set(0, 0.97, -0.3);
    group.add(ring);
    for (let i = 0; i < 5; i += 1) {
      const a = (i / 5) * Math.PI * 2;
      const spike = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.11, 4), gold);
      spike.position.set(Math.sin(a) * 0.16, 1.07, -0.3 + Math.cos(a) * 0.16);
      group.add(spike);
    }
    const gem = new THREE.Mesh(new THREE.OctahedronGeometry(0.04, 0), toy('#e0245e', 0.2, 0.2));
    gem.position.set(0, 0.97, -0.47);
    group.add(gem);
  }
  return group;
}
